import React, { FC, useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '~/components/atoms/buttons/Button';
import ModalContentTitle from '~/components/atoms/ModalContentTitle';
import { SupportCases } from '~/components/molecules/SupportModal';
import SupportCaseOption from '~/components/molecules/SupportModal/components/SupportCaseOption';
import { CancellationPath, SCREEN } from '~/modules/cancellation/constants';
import analytics from '~/services/analytics';

import commonStyles from '../../styles.module.scss';

type Props = {
  onClose: () => void;
  onNext: (nextCase: SupportCases) => void;
  onBack: () => void;
};

const CancellationCase: FC<Props> = ({ onClose, onNext, onBack }) => {
  const { t } = useTranslation('modal');
  const location = useLocation();
  const navigate = useNavigate();

  const navigateToCancellation = useCallback(() => {
    navigate(
      {
        pathname: CancellationPath,
        search: `?screen=${SCREEN.CANCEL_FORM}`,
      },
      {
        state: {
          from: location.pathname,
        },
      },
    );
  }, [navigate, location.pathname]);

  const handleClickCancel = (): void => {
    analytics.trackEvent('self-support - click need cancel');
    onClose();
    navigateToCancellation();
  };

  const handleClickOther = (): void => {
    analytics.trackEvent('self-support - click other');
    onNext(SupportCases.OTHER);
  };

  useEffect(() => {
    analytics.trackEvent('self-support - sub cancellation view');
  }, []);

  return (
    <div>
      <ModalContentTitle
        text={t('support.title', 'How can we help you?')}
        className={commonStyles.title}
      />

      <div className={commonStyles.options}>
        <SupportCaseOption onClick={handleClickCancel}>
          {t('support.cancellation', 'I want to cancel subscription')}
        </SupportCaseOption>

        <SupportCaseOption onClick={handleClickOther}>
          {t('support.other', 'I have other questions')}
        </SupportCaseOption>
      </div>

      <div className={commonStyles.buttons_container}>
        <Button color="primary" variant="contained" onClick={onClose}>
          {t('general:done', 'Done')}
        </Button>

        <Button color="primary" variant="text" onClick={onBack}>
          {t('general:back', 'Back')}
        </Button>
      </div>
    </div>
  );
};

export default CancellationCase;
